import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Mail, FileText, Users, RefreshCw, Send, Plus, Trash2, X, PenLine, Upload } from "lucide-react";

const GROUPS = [
  { id: "membres_honneur", label: "🎖️ Membres d'honneur", color: "bg-purple-100 text-purple-800" },
  { id: "sages", label: "🧓 Sages", color: "bg-amber-100 text-amber-800" },
  { id: "trustees_uk", label: "🇬🇧 Trustees UK", color: "bg-blue-100 text-blue-800" },
  { id: "bureau_togo", label: "🇹🇬 Bureau Togo", color: "bg-emerald-100 text-emerald-800" },
];

export default function BroadcastAdminPanel({ API }) {
  const pin = "SAFUAYE2024";
  const [documents, setDocuments] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({
    pdf_filename: "SAFUAYE_Programme_Academie_et_Gala_Recadre.pdf",
    subject: "SAFUAYE — Document officiel",
    message_html: "<p>Chers Membres, veuillez trouver en pièce jointe le document ci-joint.</p><p>Bien fraternellement,<br/>APEDO Kokou Ognakotan · Président International — SAFUAYE UK</p>",
    groups: ["membres_honneur", "trustees_uk"],
  });
  const [newRec, setNewRec] = useState({ group: "membres_honneur", full_name: "", email: "", role: "", country: "" });
  const [showAddRec, setShowAddRec] = useState(false);
  const [sigStatus, setSigStatus] = useState({ exists: false });
  const sigInputRef = useRef(null);

  const load = async () => {
    try {
      const [d, r, b, s] = await Promise.all([
        axios.get(`${API}/governance/documents`, { params: { pin } }),
        axios.get(`${API}/governance/recipients`, { params: { pin } }),
        axios.get(`${API}/governance/broadcasts`, { params: { pin } }),
        axios.get(`${API}/governance/signature-status`, { params: { pin } }),
      ]);
      setDocuments(d.data.items || []);
      setRecipients(r.data.items || []);
      setBroadcasts(b.data.items || []);
      setSigStatus(s.data || { exists: false });
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadSignature = async (file) => {
    if (!file) return;
    setBusy("sig-upload");
    try {
      const fd = new FormData();
      fd.append("pin", pin);
      fd.append("file", file);
      const r = await axios.post(`${API}/governance/upload-signature`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      alert(`✅ Signature enregistrée (${r.data.size_kb} Ko, ${r.data.dimensions?.[0]}×${r.data.dimensions?.[1]} px).\n\nRegénère un certificat de don ou le rapport trimestriel pour la voir apparaître.`);
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const deleteSignature = async () => {
    if (!window.confirm("Supprimer la signature scannée ? Les prochains PDF seront émis sans signature.")) return;
    try {
      await axios.delete(`${API}/governance/signature`, { params: { pin } });
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
  };

  const regenerateProgramme = async () => {
    if (!window.confirm("Régénérer le Programme Académie avec la signature actuelle ?")) return;
    setBusy("regen-prog");
    try {
      const r = await axios.post(`${API}/governance/regenerate/programme-academie`, null, { params: { pin } });
      alert(`✅ Programme régénéré (${r.data.size_kb} Ko) — signature ${r.data.has_signature ? "embarquée ✅" : "absente ⚠️"}\n\nOuverture du PDF…`);
      window.open(r.data.url, "_blank", "noopener");
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const seed = async () => {
    setBusy("seed");
    try { await axios.post(`${API}/governance/seed-recipients`, null, { params: { pin } }); load(); }
    catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const addRecipient = async () => {
    if (!newRec.full_name.trim() || !newRec.email.trim()) {
      alert("Nom + email requis"); return;
    }
    setBusy("add-rec");
    try {
      await axios.post(`${API}/governance/recipients`, { pin, ...newRec });
      setNewRec({ group: "membres_honneur", full_name: "", email: "", role: "", country: "" });
      setShowAddRec(false);
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const deleteRecipient = async (id) => {
    if (!window.confirm("Supprimer ce destinataire ?")) return;
    await axios.delete(`${API}/governance/recipients/${id}`, { params: { pin } });
    load();
  };

  const send = async () => {
    if (!form.pdf_filename || !form.groups.length) {
      alert("Choisis un PDF et au moins un groupe"); return;
    }
    // Count target recipients
    const count = recipients.filter(r => form.groups.includes(r.group)).length;
    if (!count) { alert("Aucun destinataire dans les groupes sélectionnés."); return; }
    if (!window.confirm(`Envoyer "${form.pdf_filename}" à ${count} destinataire(s) ?`)) return;
    setBusy("send");
    try {
      const r = await axios.post(`${API}/governance/broadcast`, { pin, ...form });
      alert(`✅ ${r.data.sent}/${r.data.total} envois réussis.`);
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const targetCount = recipients.filter(r => form.groups.includes(r.group)).length;

  return (
    <div className="bg-white rounded-xl p-5 shadow-lg mt-6" data-testid="broadcast-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-700" />
          Diffusion officielle — Documents en 1 clic
        </h4>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs bg-slate-200 hover:bg-slate-300 px-2 py-1 rounded">
          <RefreshCw className="w-3 h-3" /> Rafraîchir
        </button>
      </div>

      {/* Signature scannée du Président International */}
      <div className={`rounded-lg p-3 mb-4 border ${sigStatus.exists ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}
           data-testid="sig-status-block">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <PenLine className={`w-5 h-5 ${sigStatus.exists ? "text-emerald-700" : "text-amber-700"}`} />
            <div>
              <p className="text-sm font-bold text-gray-900">
                Signature scannée du Président International
              </p>
              <p className="text-[11px] text-gray-600">
                {sigStatus.exists
                  ? `Enregistrée (${sigStatus.size_kb} Ko · ${new Date(sigStatus.modified_at).toLocaleString("fr-FR")}) — embarquée sur tous les prochains PDF officiels.`
                  : "Aucune signature enregistrée. Uploade un PNG/JPG (fond blanc idéal) : le système recadre automatiquement le whitespace."}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <input type="file" accept="image/png,image/jpeg,image/webp"
                    ref={sigInputRef}
                    onChange={e => uploadSignature(e.target.files?.[0])}
                    data-testid="sig-file-input"
                    className="hidden" />
            <button onClick={() => sigInputRef.current?.click()} disabled={busy === "sig-upload"}
                    data-testid="sig-upload-btn"
                    className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded font-semibold text-white ${sigStatus.exists ? "bg-blue-700 hover:bg-blue-800" : "bg-amber-600 hover:bg-amber-700"} disabled:opacity-50`}>
              <Upload className="w-3.5 h-3.5" />
              {busy === "sig-upload" ? "..." : (sigStatus.exists ? "Remplacer" : "📝 Uploader ma signature")}
            </button>
            {sigStatus.exists && (
              <>
                <button onClick={regenerateProgramme} disabled={busy === "regen-prog"}
                        data-testid="sig-regen-prog"
                        title="Régénérer le Programme Académie avec la signature actuelle"
                        className="inline-flex items-center gap-1 text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-2 py-1.5 rounded font-semibold disabled:opacity-50">
                  📄 {busy === "regen-prog" ? "..." : "Régénérer Programme"}
                </button>
                <button onClick={deleteSignature}
                        data-testid="sig-delete-btn"
                        className="inline-flex items-center gap-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 px-2 py-1.5 rounded font-semibold">
                  <Trash2 className="w-3 h-3" /> Retirer
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 2-column layout: formulaire + recipients */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* LEFT — Compose */}
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase text-slate-700 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> Composer un envoi
          </p>
          <label className="block text-xs">
            <span className="font-semibold text-gray-700">Document PDF</span>
            <select value={form.pdf_filename}
                    onChange={e => setForm({ ...form, pdf_filename: e.target.value })}
                    data-testid="bc-pdf-select"
                    className="w-full border rounded px-2 py-1.5 text-xs mt-0.5">
              <option value="">— Choisir un document —</option>
              {documents.map(d => (
                <option key={d.filename} value={d.filename}>
                  {d.filename} ({Math.round(d.size / 1024)} Ko)
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-gray-700">Sujet</span>
            <input value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })}
                    data-testid="bc-subject"
                    className="w-full border rounded px-2 py-1.5 text-xs mt-0.5" />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-gray-700">Message HTML</span>
            <textarea value={form.message_html} rows={4}
                       onChange={e => setForm({ ...form, message_html: e.target.value })}
                       data-testid="bc-message"
                       className="w-full border rounded px-2 py-1.5 text-xs mt-0.5 font-mono" />
          </label>
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Groupes destinataires</p>
            <div className="flex flex-wrap gap-1">
              {GROUPS.map(g => {
                const active = form.groups.includes(g.id);
                const cnt = recipients.filter(r => r.group === g.id).length;
                return (
                  <button key={g.id}
                          onClick={() => setForm({
                            ...form,
                            groups: active ? form.groups.filter(x => x !== g.id) : [...form.groups, g.id]
                          })}
                          data-testid={`bc-group-${g.id}`}
                          className={`text-[10px] px-2 py-1 rounded-full border-2 transition ${
                            active ? "border-blue-700 " + g.color : "border-slate-200 text-slate-500"
                          }`}>
                    {g.label} · {cnt}
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={send} disabled={busy === "send" || !form.pdf_filename || !targetCount}
                  data-testid="bc-send"
                  className="w-full inline-flex items-center justify-center gap-1 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded text-sm font-bold disabled:opacity-50">
            <Send className="w-4 h-4" /> {busy === "send" ? "Envoi..." : `Envoyer à ${targetCount} destinataire(s)`}
          </button>
        </div>

        {/* RIGHT — Recipients list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase text-slate-700 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Destinataires ({recipients.length})
            </p>
            <div className="flex gap-1">
              <button onClick={() => setShowAddRec(!showAddRec)}
                      data-testid="bc-add-rec-toggle"
                      className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold">
                <Plus className="w-3 h-3 inline" /> Ajouter
              </button>
              {recipients.length === 0 && (
                <button onClick={seed} disabled={busy === "seed"}
                        data-testid="bc-seed"
                        className="text-[10px] bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded font-semibold disabled:opacity-50">
                  {busy === "seed" ? "..." : "Seed défauts"}
                </button>
              )}
            </div>
          </div>
          {showAddRec && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 space-y-1" data-testid="bc-add-rec-form">
              <div className="grid grid-cols-2 gap-1 text-xs">
                <select value={newRec.group} onChange={e => setNewRec({ ...newRec, group: e.target.value })} className="border rounded px-1 py-1" data-testid="bc-new-group">
                  {GROUPS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                </select>
                <input placeholder="Pays" value={newRec.country} onChange={e => setNewRec({ ...newRec, country: e.target.value })} className="border rounded px-1 py-1" data-testid="bc-new-country" />
                <input placeholder="Nom complet *" value={newRec.full_name} onChange={e => setNewRec({ ...newRec, full_name: e.target.value })} className="border rounded px-1 py-1 col-span-2" data-testid="bc-new-name" />
                <input placeholder="Email *" value={newRec.email} onChange={e => setNewRec({ ...newRec, email: e.target.value })} className="border rounded px-1 py-1 col-span-2" data-testid="bc-new-email" />
                <input placeholder="Rôle" value={newRec.role} onChange={e => setNewRec({ ...newRec, role: e.target.value })} className="border rounded px-1 py-1 col-span-2" />
              </div>
              <button onClick={addRecipient} disabled={busy === "add-rec"}
                      data-testid="bc-add-rec-save"
                      className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold py-1 rounded disabled:opacity-50">
                {busy === "add-rec" ? "..." : "💾 Enregistrer"}
              </button>
            </div>
          )}
          <div className="max-h-80 overflow-y-auto border rounded">
            {recipients.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-4">
                Aucun destinataire. Clique sur <em>Seed défauts</em> ou <em>Ajouter</em>.
              </p>
            ) : recipients.map(r => (
              <div key={r.id} className="flex items-center gap-1 border-b px-2 py-1.5 text-xs hover:bg-slate-50" data-testid={`bc-rec-${r.id}`}>
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${GROUPS.find(g => g.id === r.group)?.color || "bg-gray-100"}`}>
                  {GROUPS.find(g => g.id === r.group)?.label.slice(0, 3) || r.group}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{r.full_name}</p>
                  <p className="text-[10px] text-gray-500 truncate">{r.email} · {r.role || "—"}</p>
                </div>
                <button onClick={() => deleteRecipient(r.id)} className="text-red-600 hover:bg-red-50 p-1 rounded">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Historique broadcasts */}
      {broadcasts.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold uppercase text-slate-700 mb-1">Historique récent</p>
          <div className="text-[11px] space-y-0.5 max-h-32 overflow-y-auto">
            {broadcasts.slice(0, 8).map(b => (
              <div key={b.id} className="border-b py-1" data-testid={`bc-history-${b.id}`}>
                <span className="font-mono text-gray-500">{new Date(b.created_at).toLocaleString("fr-FR")}</span>
                {" · "}
                <span className={`font-semibold ${b.sent_count === b.total_count ? "text-emerald-700" : "text-amber-700"}`}>
                  {b.sent_count}/{b.total_count}
                </span>
                {" · "}
                <span className="text-gray-700">{b.pdf_filename}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
